// F-WEB-021 /admin/notificacoes — caixa de entrada paginada + filtros.
// Redesign 2026-09-22 (W2): feed com ponto de nao lida, chip de tipo e data
// em nowrap, "Marcar lida" em linha propria (handoff §16). Status/Tipo/Limite
// via FilterPanel — os 3 sao grupos de opcao unica e continuam aplicando NA
// HORA (comportamento preservado, plano §6.4: "filtros aplicam na hora").
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCheck } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { AppNotification } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Card, CardContent } from "../../components/ui/card";
import { StatusDot } from "../../components/ui/StatusDot";
import { FilterPanel, type FilterGroup } from "../../components/ui/FilterPanel";
import { formatDate } from "../../lib/utils";
import { TYPE_LABEL, labelNotification, notificationLink, summarizeNotification } from "../../lib/notifications";

const LIMIT_OPTIONS = ["20", "50", "100", "200"];

export function AdminNotificationsPage(): JSX.Element {
  const qc = useQueryClient();
  const [readFilter, setReadFilter] = useState<"" | "true" | "false">("");
  const [typeFilter, setTypeFilter] = useState("");
  const [limit, setLimit] = useState(50);

  const queryParams = new URLSearchParams();
  queryParams.set("limit", String(limit));
  if (readFilter) queryParams.set("read", readFilter);
  if (typeFilter) queryParams.set("type", typeFilter);

  const { data = [], isLoading, isError, error } = useQuery<AppNotification[]>({
    queryKey: ["admin-notifications", readFilter, typeFilter, limit],
    queryFn: async () =>
      (await api.get<AppNotification[]>(`/api/notifications?${queryParams.toString()}`)).data,
  });

  const markRead = useMutation({
    mutationFn: async ({ id, read }: { id: string; read: boolean }) => {
      await api.patch(`/api/notifications/${id}`, { read });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
  });

  const markAll = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ updated: number }>("/api/notifications/mark-all-read", {});
      return r.data;
    },
    onSuccess: (d) => {
      toast.success(`${d.updated} notificações marcadas como lidas`);
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      qc.invalidateQueries({ queryKey: ["notif-unread-count"] });
      qc.invalidateQueries({ queryKey: ["notif-list"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const groups: FilterGroup[] = [
    {
      key: "read",
      label: "Status",
      options: [
        { value: "false", label: "Não lidas" },
        { value: "true", label: "Lidas" },
      ],
    },
    {
      key: "type",
      label: "Tipo",
      options: (Object.keys(TYPE_LABEL) as (keyof typeof TYPE_LABEL)[]).map((v) => ({
        value: v,
        label: TYPE_LABEL[v],
      })),
    },
    {
      key: "limit",
      label: "Limite",
      options: LIMIT_OPTIONS.map((v) => ({ value: v, label: v })),
    },
  ];

  const value = { read: readFilter || null, type: typeFilter || null, limit: String(limit) };

  const onChange = (key: string, v: string | null) => {
    if (key === "read") setReadFilter((v ?? "") as "" | "true" | "false");
    else if (key === "type") setTypeFilter(v ?? "");
    else if (key === "limit") setLimit(Number(v ?? "50"));
  };

  const onClear = () => {
    setReadFilter("");
    setTypeFilter("");
    setLimit(50);
  };

  return (
    <div>
      <PageHeader
        title="Caixa de entrada"
        description="Notificações do sistema: novos SKUs, falhas de sincronização, rollback, etc."
        actions={
          <Button variant="secondary" onClick={() => markAll.mutate()} loading={markAll.isPending}>
            <CheckCheck className="icon" aria-hidden /> Marcar tudo lido
          </Button>
        }
      />

      <div className="with-filters">
        <FilterPanel groups={groups} value={value} onChange={onChange} onClear={onClear} />

        <div className="with-filters__body">
          {isLoading ? (
            <Card><CardContent className="stack"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></CardContent></Card>
          ) : isError ? (
            <EmptyState title="Erro" description={apiErrorMessage(error)} />
          ) : data.length === 0 ? (
            <EmptyState title="Nenhuma notificação" />
          ) : (
            // Handoff §16: "UMA lista num cartão com divisórias" — nao um
            // cartao por item com borda de acento no topo (essa borda,
            // presa so na aresta superior de cada `.row-card` arredondado,
            // e o "torto" reportado: acento reto que nao acompanha o raio
            // do cartao). O ponto de nao-lida (StatusDot) ja carrega esse
            // sinal sozinho; card unico + divisor inferior por linha.
            <Card style={{ padding: 0 }}>
              {data.map((n, i) => (
                <div
                  key={n.id}
                  className="flex items-start gap-3"
                  style={{
                    padding: "1rem",
                    borderBottom: i < data.length - 1 ? ".0625rem solid var(--color-line)" : undefined,
                  }}
                >
                  {!n.read ? <StatusDot tone="warn" /> : <span style={{ width: ".5rem", flexShrink: 0 }} aria-hidden />}
                  <div className="stack" style={{ flex: 1, minWidth: 0 }}>
                    <div className="cluster">
                      <span className="chip chip--mono">{labelNotification(n)}</span>
                      {n.channel_slug ? <span className="chip">{n.channel_slug}</span> : null}
                      <span style={{ whiteSpace: "nowrap", fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                        {formatDate(n.created_at)}
                      </span>
                      {n.read_at ? (
                        <span style={{ whiteSpace: "nowrap", fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                          · lida em {formatDate(n.read_at)}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: "var(--text-md)", wordBreak: "break-word" }}>
                      {summarizeNotification(n)}
                    </div>
                    {notificationLink(n) ? (
                      <div>
                        <a
                          href={notificationLink(n)!.href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-coral underline underline-offset-2 hover:no-underline"
                        >
                          {notificationLink(n)!.label}
                        </a>
                      </div>
                    ) : null}
                    <div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markRead.mutate({ id: n.id, read: !n.read })}
                        disabled={markRead.isPending}
                      >
                        {n.read ? "Marcar não lida" : "Marcar lida"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
